/**
 * # 系统链接/动作管理系统
 *
 * ## 为什么这样设计
 *
 * 品牌后台（brand-admin）的装修页面中，用户配置的链接（如图片链接、导航链接）需要
 *在多个 H5 端（会员端 member、积分商城端 mall、扫码端 h5-scan）真实跳转。
 *
 * 旧方案的问题：
 * - 只有一个 `system` 类别，无法区分链接归属哪个端
 * - 存储格式为纯字符串 `"system:POINTS_MALL"`，无法携带参数（如商品ID）和守卫（如登录检查）
 * - 无法扩展到扫码操作、弹窗操作等非导航行为
 *
 * 新方案的核心思路：
 * 1. 从 "链接" 提升为 "动作" —— 动作类型包括 navigate(导航)、scan(扫码)、dialog(弹窗)、custom(自定义)
 * 2. 按端分组定义导航链接 —— member 端有自己的 HOME/MY_ACCOUNT，mall 端也有自己的 HOME/MY_ACCOUNT
 * 3. 存储格式改为 JSON 字符串 —— 可以携带 params 参数和 guards 守卫
 * 4. 所有显示名称采用 i18n key —— 后台支持多语言，不能硬编码中文
 *
 * ## 一步一步怎么做到的
 *
 * ### 第一步：定义链接
 *
 * 在 [systemLinkCategories](/packages/shared/constants/systemLinks.js#L26) 中按端分组定义导航链接：
 * - 每端有个 nameKey（i18n key，如 `systemLinks.end.member`）
 * - 每个链接有 key、nameKey、route（真正跳转路径）
 * - 可选 guards 表示导航前需要检查的条件（如 `['login']` 表示需登录）
 * - 可选 hasParams 表示该链接需要携带参数
 *
 * ### 第二步：后台选择
 *
 * brand-admin 的 [LinkSelector](/packages/brand-admin/src/components/LinkSelector/index.vue) 组件展示这些定义：
 * - 先选动作类型（页面跳转/扫码/弹窗/自定义）
 * - 选 "页面跳转" 后，再按端分组选具体链接
 * - 选完后调用 [buildActionValue](/packages/shared/constants/systemLinks.js#L116) 生成 JSON 字符串
 * - 这个 JSON 字符串存入后端数据库的 `item.link` 字段
 *
 * ### 第三步：后端存储
 *
 * 后端存储的是 JSON 对象，直接存入后端数据库的 `item.link` 字段，例如：
 * ```json
 * { "type": "navigate", "end": "member", "key": "MY_ACCOUNT", "params": {}, "guards": ["login"], "extra": {} }
 * ```
 * ```json
 * { "type": "navigate", "end": "mall", "key": "GOODS_DETAIL", "params": { "id": "xxx" }, "guards": [], "extra": {} }
 * ```
 * ```json
 * { "type": "scan", "extra": {} }
 * ```
 * ```json
 * { "type": "dialog", "extra": { "title": "xxx", "content": "xxx" } }
 * ```
 * ```json
 * { "type": "custom", "extra": { "url": "https://example.com" } }
 * ```
 *
 * ### 第四步：H5 端执行
 *
 * H5 端（member/mall/h5-scan）读取装修数据时，item.link 就是上面的 JSON 对象：
 * 1. 调用 [executeAction](/packages/shared/constants/systemLinks.js#L182) 直接传入该对象
 * 2. executeAction 内部调 [parseActionValue](/packages/shared/constants/systemLinks.js#L94) 标准化（兼容对象和字符串）
 * 3. 根据 type 分派到不同逻辑分支：
 *    - navigate：通过 [getSystemLinkRoute](/packages/shared/constants/systemLinks.js#L159) 从 systemLinkCategories 中找到 route → `uni.navigateTo`
 *    - scan：→ `uni.scanCode`
 *    - dialog：→ `uni.showModal`
 *    - custom：→ webview 打开 URL
 *
 * ## 怎么新增/删除/修改
 *
 * ### 一句话让 AI 执行
 *
 * 以下指令直接在 AI 输入框输入，AI 会自动执行 [manage-system-links skill](/skills/conventions/manage-system-links/SKILL.md) 完成所有操作：
 *
 * | 操作 | 在 AI 输入框输入的指令（key 由 AI 推断，无需提供） |
 * | --- | --- |
 * | 新增端 | `新增一个经销商端，包含首页和我的订单页面，都需要登录` |
 * | 新增链接 | `给会员端新增一个我的钱包页面，路由 /pages/wallet/index，需要登录` |
 * | 删除链接 | `删除会员端的我的账户链接` |
 * | 修改链接 | `把积分商城的首页路由改为 /pages/home/new` |
 * | 删除端 | `删除经销商端` |
 *
 * AI 会：1) 检查是否已存在 → 2) 推断 key → 3) 改 systemLinkCategories → 4) 改三个语言文件
 * → 5) 运行 pnpm lint 验证 → 6) 输出操作结果（成功/失败/已存在详情）
 *
 * ### 涉及文件
 *
 * | 文件 | 说明 |
 * | --- | --- |
 * | `packages/shared/constants/systemLinks.js` | 核心定义：systemLinkCategories、工具函数 |
 * | `packages/shared/constants/index.js` | 导出入口（一般不改） |
 * | `packages/brand-admin/src/locales/lang/{locale}/systemLinks.js` | 三个语言：zh-CN / en-US / ja |
 *
 * ### 数据格式速查
 *
 * ```js
 * // 每个端的结构
 * {
 *   key: 'member',                     // 端标识，全局唯一
 *   nameKey: 'systemLinks.end.member', // i18n key
 *   links: [
 *     {
 *       key: 'MY_ACCOUNT',             // 链接 key，同端内唯一
 *       nameKey: 'systemLinks.link.home', // i18n key
 *       route: '/pages/profile/index',         // uni-app 页面路径
 *       guards: ['login'],             // 可选，守卫类型
 *       hasParams: true,               // 可选，是否需要携带参数
 *     },
 *   ],
 * }
 * ```
 *
 * ---
 *
 * ### 新增一个端（如新增 "经销商端" dealer）
 *
 * 1. 在 systemLinkCategories 数组末尾追加对象
 * 2. 在三个语言文件的 `end` 下添加对应 key
 * 3. 运行 `pnpm lint` 验证
 *
 * ```js
 * // systemLinks.js 追加
 * {
 *   key: 'dealer',
 *   nameKey: 'systemLinks.end.dealer',
 *   links: [
 *     { key: 'HOME', nameKey: 'systemLinks.link.home', route: '/pages/home/index' },
 *     { key: 'SCAN', nameKey: 'systemLinks.link.scan', route: '/pages/profile/h5scan/index', guards: ['login'] },
 *   ],
 * }
 *
 * // zh-CN/systemLinks.js:  dealer: '经销商端'
 * // en-US/systemLinks.js:   dealer: 'Dealer Portal'
 * // ja/systemLinks.js:      dealer: 'ディーラーポータル'
 * ```
 *
 * ---
 *
 * ### 新增一个链接
 *
 * 1. 在对应端的 links 数组中追加一个 link 对象
 * 2. 在三个语言文件的 `link` 下添加对应 key
 * 3. 运行 `pnpm lint` 验证
 *
 * ```js
 * // links 数组中追加
 * { key: 'MY_WALLET', nameKey: 'systemLinks.link.myWallet', route: '/pages/wallet/index', guards: ['login'] }
 * ```
 *
 * ---
 *
 * ### 删除一个链接
 *
 * 1. 从对应端的 links 数组中移除该 link 对象
 * 2. （可选）从三个语言文件的 `link` 下移除对应 key
 * 3. 运行 `pnpm lint` 验证
 *
 * ---
 *
 * ### 修改链接
 *
 * 直接修改 systemLinkCategories 中对应 link 对象的字段：
 *
 * | 改什么 | 改哪个字段 | 说明 |
 * | --- | --- | --- |
 * | 跳转页面 | `route` | 改路径即可 |
 * | 登录守卫 | `guards` | 增删 `'login'` |
 * | 是否需要参数 | `hasParams` | `true` / `false` |
 * | 显示名称 | `nameKey` | 同步更新三个语言文件 |
 *
 * 所有 H5 端通过 getSystemLinkRoute 读取的是同一数据源，修改立即生效。
 *
 * ---
 *
 * ### 删除一个端
 *
 * 1. 从 systemLinkCategories 数组中移除整个端对象
 * 2. （可选）从三个语言文件的 `end` 和 `link` 下移除对应 key
 * 3. 运行 `pnpm lint` 验证
 *
 * ---
 *
 * ### 执行原则
 *
 * 1. **成对修改**：改 systemLinkCategories 必须同步改三个语言文件中的 i18n key
 * 2. **唯一性**：端 key 全局唯一，链接 key 同端内唯一
 * 3. **路由存在性**：`route` 填写前确认对应 H5 端 `pages.json` 中存在该页面路径
 * 4. **后卫兵**：增加 `guards: ['login']` 前确认该路由在相关端中需要登录
 * 5. **最后验证**：运行 `pnpm lint`
 *
 * ## 数据流总结
 *
 *
 * systemLinkCategories (定义)
 *        │
 *        ├── LinkSelector.vue (brand-admin 后台选择)
 *        │       └── buildActionValue() → JSON 对象
 *        │               └── 存入后端 item.link
 *        │
 *        └── executeAction() (H5 端执行)
 *                └── parseActionValue() 标准化
 *                        └── getSystemLinkRoute() 查路由
 *                                └── uni.navigateTo() 跳转
 *
 * 从 "定义 → 选择 → 存储 → 解析 → 执行" 全链路打通，
 * brand-admin 负责生产动作值，各 H5 端负责消费和执行。
 */

/**
 * 系统链接/动作管理
 *
 * 从"链接值"提升为"动作值"，支持四种动作类型：
 * - navigate: 系统链接导航跳转
 * - scan: 扫码操作
 * - dialog: 弹窗操作
 * - custom: 自定义链接跳转
 *
 * 后端存储格式为 JSON 对象，直接存入组件配置的 link 字段。
 */

import { cache } from '../utils/cache.js';
import { STORAGE_KEYS } from './index.js';

// ==================== 动作类型常量 ====================

export const ACTION_TYPE = Object.freeze({
  NAVIGATE: 'navigate',
  SCAN: 'scan',
  DIALOG: 'dialog',
  CUSTOM: 'custom',
});

/** 默认展示的动作类型（排除 dialog） */
export const DEFAULT_ACTION_TYPES = [
  ACTION_TYPE.NAVIGATE,
  ACTION_TYPE.SCAN,
  ACTION_TYPE.CUSTOM,
];

// ==================== 系统链接分类定义（仅用于 navigate 类型） ====================

/**
 * 按端分组的导航链接定义
 * 所有显示名称使用 i18n key（nameKey），由消费方通过 t() 翻译
 */
export const systemLinkCategories = [
  {
    key: 'member',
    nameKey: 'systemLinks.end.member',
    basePath: '/member/',
    links: [
      { key: 'HOME', nameKey: 'systemLinks.link.home', route: '/pages/profile/index' },
      {
        key: 'SCAN',
        nameKey: 'systemLinks.link.scan',
        route: '/pages/profile/h5scan/index',
        guards: ['login'],
      },
      {
        key: 'MY_GIFTS',
        nameKey: 'systemLinks.link.myGifts',
        route: '/pages/profile/gifts/index',
        guards: ['login'],
      },
      {
        key: 'MY_POINT_ORDERS',
        nameKey: 'systemLinks.link.myPointOrders',
        route: '/pages/profile/pointOrders/index',
        guards: ['login'],
      },
      {
        key: 'AFTERSALE',
        nameKey: 'systemLinks.link.afterSale',
        route: '/pages/profile/aftersale/index',
        guards: ['login'],
      },
      {
        key: 'GROWTH',
        nameKey: 'systemLinks.link.growth',
        route: '/pages/profile/growth/index',
        guards: ['login'],
      },
      {
        key: 'BENEFITS',
        nameKey: 'systemLinks.link.benefits',
        route: '/pages/profile/benefits/index',
        guards: ['login'],
      },
    ],
  },
  {
    key: 'pmall',
    nameKey: 'systemLinks.end.mall',
    basePath: '/pmall/',
    links: [
      { key: 'HOME', nameKey: 'systemLinks.link.home', route: '/pages/home/index' },
    ],
  },
  {
    key: 'fwsy',
    nameKey: 'systemLinks.end.h5Scan',
    basePath: '/fwsy/',
    links: [
      { key: 'HOME', nameKey: 'systemLinks.link.home', route: '/pages/index/index' },
      { key: 'LOGIN', nameKey: 'systemLinks.link.login', route: '/pages/login/index' },
    ],
  },
];

// ==================== 扫码类型定义（按端分组） ====================

/**
 * 扫码动作的子类型定义（按端分组）
 * 各端可根据业务需求定义不同的扫码行为
 * 所有显示名称使用 i18n key（nameKey），由消费方通过 t() 翻译
 */
export const scanCategories = [
  {
    key: 'member',
    nameKey: 'systemLinks.end.member',
    scanTypes: [
      { key: 'SCAN_VERIFY', nameKey: 'systemLinks.link.scan' },
    ],
  },
  {
    key: 'pmall',
    nameKey: 'systemLinks.end.mall',
    scanTypes: [],
  },
  {
    key: 'fwsy',
    nameKey: 'systemLinks.end.h5Scan',
    scanTypes: [],
  },
];

// ==================== 弹窗操作类型定义（按端分组） ====================

/**
 * 弹窗动作的子类型定义（按端分组）
 * 各端可定义不同的弹窗模板，当前暂无预定义类型，供未来扩展
 * 所有显示名称使用 i18n key（nameKey），由消费方通过 t() 翻译
 */
export const dialogCategories = [
  { key: 'member', nameKey: 'systemLinks.end.member', dialogTypes: [] },
  { key: 'pmall', nameKey: 'systemLinks.end.mall', dialogTypes: [] },
  { key: 'fwsy', nameKey: 'systemLinks.end.h5Scan', dialogTypes: [] },
];

// ==================== 自定义链接类型定义（按端分组） ====================

/**
 * 自定义链接的子类型定义（按端分组）
 * 各端可定义不同的自定义链接预设，当前暂无预定义类型，供未来扩展
 * 所有显示名称使用 i18n key（nameKey），由消费方通过 t() 翻译
 */
export const customCategories = [
  { key: 'member', nameKey: 'systemLinks.end.member', customTypes: [] },
  { key: 'pmall', nameKey: 'systemLinks.end.mall', customTypes: [] },
  { key: 'fwsy', nameKey: 'systemLinks.end.h5Scan', customTypes: [] },
];

// ==================== TabBar 页面路由（各端 pages.json 的 tabBar.list） ====================

/** 所有端的 TabBar 页面路由集合，navigate 时需要走 switchTab 而非 navigateTo */
const TABBAR_ROUTES = new Set([
  '/pages/home/index',
]);

// ==================== 构建类别名称映射（i18n key） ====================

/** 端名称 key 映射：{ endKey: nameKey } */
export const systemLinkEndNameKeys = {};
systemLinkCategories.forEach(category => {
  systemLinkEndNameKeys[category.key] = category.nameKey;
});

/** 链接名称 key 映射：{ linkKey: nameKey } */
export const systemLinkNameKeys = {};
systemLinkCategories.forEach(category => {
  category.links.forEach(link => {
    systemLinkNameKeys[link.key] = link.nameKey;
  });
});

/** 路由映射：{ linkKey: route } */
export const systemLinkRoutes = {};
systemLinkCategories.forEach(category => {
  category.links.forEach(link => {
    systemLinkRoutes[link.key] = link.route;
  });
});

/** 扫码类型名称 key 映射：{ scanTypeKey: nameKey } */
export const scanNameKeys = {};
scanCategories.forEach(endScan => {
  endScan.scanTypes.forEach(scanType => {
    scanNameKeys[scanType.key] = scanType.nameKey;
  });
});

/** 端基础路径映射：{ endKey: basePath }，用于跨端跳转时构建完整 URL */
const END_BASE_PATHS = {};
systemLinkCategories.forEach(category => {
  if (category.basePath) {
    END_BASE_PATHS[category.key] = category.basePath;
  }
});

/**
 * 获取当前 H5 端标识
 * 优先使用构建时注入的 __APP_END__（各端 vite.config.js 的 define），
 * 回退到通过 window.location.pathname 匹配 basePath
 * @returns {string} 端标识（如 'member'、'pmall'、'fwsy'），未匹配返回空字符串
 */
function getCurrentEnd() {
  /* global __APP_END__ */
  if (typeof __APP_END__ !== 'undefined') {
    return __APP_END__;
  }
  // eslint-disable-next-line no-restricted-globals
  if (typeof window === 'undefined') return '';
  // eslint-disable-next-line no-restricted-globals
  const pathname = window.location.pathname;
  for (const cat of systemLinkCategories) {
    if (cat.basePath && pathname.startsWith(cat.basePath)) {
      return cat.key;
    }
  }
  return '';
}

/**
 * 构建跨端跳转的完整 URL
 * H5 URL 格式：域名/basePath#/routePath?params
 * @param {string} targetEnd - 目标端标识
 * @param {string} routePath - 页面路由路径
 * @param {Object} [params] - URL 参数
 * @returns {string} 完整 URL，失败返回空字符串
 */
function buildCrossEndUrl(targetEnd, routePath, params) {
  const basePath = END_BASE_PATHS[targetEnd];
  // eslint-disable-next-line no-restricted-globals
  if (!basePath || typeof window === 'undefined') return '';
  // eslint-disable-next-line no-restricted-globals
  const origin = window.location.origin;
  let url = `${origin}${basePath}#${routePath}`;
  if (params && Object.keys(params).length > 0) {
    const query = Object.entries(params)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    url += `?${query}`;
  }
  return url;
}

// ==================== 工具函数 ====================

/**
 * 解析动作值
 * 兼容传入 JSON 对象或 JSON 字符串，统一返回标准化对象
 * @param {string|Object} value - 动作值对象或 JSON 字符串
 * @returns {{ type: string, end: string, key: string, params: Object, guards: string[], extra: Object } | null}
 */
export function parseActionValue(value) {
  if (!value) return null;
  let obj;
  if (typeof value === 'string') {
    try {
      obj = JSON.parse(value);
    } catch {
      return null;
    }
  } else {
    obj = value;
  }
  return {
    type: obj.type || '',
    end: obj.end || '',
    key: obj.key || '',
    params: obj.params || {},
    guards: obj.guards || [],
    extra: obj.extra || {},
  };
}

/**
 * 构建动作值
 * @param {{ type: string, end?: string, key?: string, params?: Object, guards?: string[], extra?: Object }} actionObj
 * @returns {Object} 动作值对象（直接存入后端）
 */
export function buildActionValue(actionObj) {
  if (!actionObj || !actionObj.type) return null;
  const { type, end, key, params, guards, extra } = actionObj;
  const obj = { type };
  if (end) obj.end = end;
  if (key) obj.key = key;
  if (params && Object.keys(params).length > 0) obj.params = params;
  if (guards && guards.length > 0) obj.guards = guards;
  if (extra && Object.keys(extra).length > 0) obj.extra = extra;
  return obj;
}

/**
 * 获取动作显示名称的 i18n key
 * @param {string|Object} value - 动作值对象或 JSON 字符串
 * @returns {string} i18n key
 */
export function getActionNameKey(value) {
  const parsed = parseActionValue(value);
  if (!parsed) return '';
  switch (parsed.type) {
    case ACTION_TYPE.NAVIGATE: {
      const endKey = systemLinkEndNameKeys[parsed.end] || '';
      const linkKey = systemLinkNameKeys[parsed.key] || '';
      return [endKey, linkKey].filter(Boolean).join('/');
    }
    case ACTION_TYPE.SCAN: {
      return (parsed.extra?.scanType && scanNameKeys[parsed.extra.scanType]) || 'systemLinks.actionType.scan';
    }
    case ACTION_TYPE.DIALOG:
      return 'systemLinks.actionType.dialog';
    case ACTION_TYPE.CUSTOM:
      return 'systemLinks.actionType.custom';
    default:
      return '';
  }
}

/**
 * 根据端和 key 查找路由
 * @param {string} end - 端标识
 * @param {string} key - 链接 key
 * @returns {string|undefined} 路由路径
 */
export function getSystemLinkRoute(end, key) {
  if (!end || !key) return undefined;
  const category = systemLinkCategories.find(cat => cat.key === end);
  if (!category) return undefined;
  const link = category.links.find(l => l.key === key);

  return {
    route: link?.route || '',
    category: category,
  };
}

// ==================== 动作执行 ====================

/**
 * 执行同端导航（当前端内 uni.navigateTo/switchTab 跳转）
 * 内部函数，不导出
 */
function executeSameNavigate(parsed) {
  if (parsed.guards.includes('login')) {
    const token = getToken();
    if (!token) {
      uni.navigateTo({ url: '/pages/login/index' });
      return;
    }
  }

  const routeInfo = getSystemLinkRoute(parsed.end, parsed.key);
  const routePath = routeInfo?.route || '';
  if (!routePath) return;

  let url = routePath;
  if (parsed.params && Object.keys(parsed.params).length > 0) {
    const query = Object.entries(parsed.params)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    url += `?${query}`;
  }

  if (TABBAR_ROUTES.has(routePath)) {
    uni.switchTab({ url });
  } else {
    uni.navigateTo({ url });
  }
}

/**
 * 执行跨端导航（window.location.href 跳转）
 * 内部函数，不导出
 */
function executeCrossNavigate(parsed) {
  if (parsed.guards.includes('login')) {
    const token = getToken();
    if (!token) {
      uni.navigateTo({ url: '/pages/login/index' });
      return;
    }
  }

  const routeInfo = getSystemLinkRoute(parsed.end, parsed.key);
  const routePath = routeInfo?.route || '';
  if (!routePath) return;

  const crossParams = { ...(parsed.params || {}) };
  const brandNo = cache.get(STORAGE_KEYS.BRAND_NO);
  if (brandNo) {
    crossParams.brandNo = brandNo;
  }
  if (parsed.guards.includes('login') && !crossParams.token) {
    const token = cache.get(STORAGE_KEYS.TOKEN);
    if (token) {
      // 同域名暂时不需要带token
      // crossParams.token = token;
    }
  }
  const fullUrl = buildCrossEndUrl(parsed.end, routePath, crossParams);
  if (fullUrl) {
    // eslint-disable-next-line no-restricted-globals
    window.location.href = fullUrl;
  }
}

/**
 * 执行同端扫码
 * - SCAN_VERIFY → navigateTo h5scan 页面
 * - 其他场景 → uni.scanCode() 触发真实扫码
 * 内部函数，不导出
 */
function executeSameScan(parsed) {
  if (parsed.extra?.scanType === 'SCAN_VERIFY') {
    const routeInfo = getSystemLinkRoute(parsed.end || 'member', 'SCAN');
    const routePath = routeInfo?.route || '';
    if (routePath) {
      uni.navigateTo({ url: routePath });
    }
    return;
  }
  uni.scanCode({ ...parsed.extra });
}

/**
 * 执行跨端扫码（跳转到对应端的 h5scan 页面）
 * 目前仅 member 有扫码定义，跨端时拼接完整 URL 跳转
 * 内部函数，不导出
 */
function executeCrossScan(parsed) {
  const targetEnd = parsed.end || 'member';
  const routeInfo = getSystemLinkRoute(targetEnd, 'SCAN');
  const routePath = routeInfo?.route || '';
  if (!routePath) return;

  const crossParams = { ...(parsed.params || {}) };
  const brandNo = cache.get(STORAGE_KEYS.BRAND_NO);
  if (brandNo) {
    crossParams.brandNo = brandNo;
  }
  const fullUrl = buildCrossEndUrl(targetEnd, routePath, crossParams);
  if (fullUrl) {
    // eslint-disable-next-line no-restricted-globals
    window.location.href = fullUrl;
  }
}

/** 内部函数，不导出 */
function executeSameDialog(parsed) {
  showDialog(parsed.extra);
}

/** 内部函数，不导出（当前与同端逻辑相同，后续可按需扩展） */
function executeCrossDialog(parsed) {
  showDialog(parsed.extra);
}

/** 内部函数，不导出 */
function executeCustomAction(parsed, options = {}) {
  const { webviewPath = '/pages/webview/index' } = options;
  const url = parsed.extra?.url || '';
  if (url) {
    // eslint-disable-next-line no-restricted-globals
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-restricted-globals
      window.location.href = url;
    } else {
      uni.navigateTo({ url: `${webviewPath}?url=${encodeURIComponent(url)}` });
    }
  }
}

/**
 * 执行动作
 *
 * 根据动作类型分派不同的行为：
 * - navigate: 系统链接导航跳转（含 guards 检查和 params 拼接）
 * - scan: 触发 uni-app 扫码
 * - dialog: 从 extra 读取配置显示弹窗
 * - custom: 通过 webview 打开外部 URL
 *
 * @param {string|Object} actionValue - 动作值对象或 JSON 字符串
 * @param {Object} [options] - 配置项
 * @param {string} [options.webviewPath='/pages/webview/index'] - webview 页面路径
 */
export function executeAction(actionValue, options = {}) {
  const parsed = parseActionValue(actionValue);
  if (!parsed || !parsed.type) return;

  const currentEnd = getCurrentEnd();
  const isCrossEnd = currentEnd && parsed.end && currentEnd !== parsed.end;

  switch (parsed.type) {
    case ACTION_TYPE.NAVIGATE:
      return isCrossEnd ? executeCrossNavigate(parsed) : executeSameNavigate(parsed);
    case ACTION_TYPE.SCAN:
      return isCrossEnd ? executeCrossScan(parsed) : executeSameScan(parsed);
    case ACTION_TYPE.DIALOG:
      return isCrossEnd ? executeCrossDialog(parsed) : executeSameDialog(parsed);
    case ACTION_TYPE.CUSTOM:
      return executeCustomAction(parsed, options);
  }
}

/**
 * 快捷构建导航动作值
 * @param {string} end - 端标识
 * @param {string} key - 链接 key
 * @param {Object} [params] - URL 参数
 * @param {string[]} [guards] - 守卫列表
 * @returns {Object} 动作值对象
 */
export function buildNavigateLinkValue(end, key, params, guards) {
  return buildActionValue({ type: ACTION_TYPE.NAVIGATE, end, key, params, guards });
}

// ==================== 辅助函数 ====================

/**
 * 获取 token（兼容 uniapp 和浏览器环境）
 */
function getToken() {
  try {
    const token = uni.getStorageSync('zy_token');
    return token || null;
  } catch {
    return null;
  }
}

/**
 * 显示弹窗
 * @param {Object} config - { title, content, ... }
 */
function showDialog(config = {}) {
  const { title, content } = config;
  uni.showModal({
    title: title || '',
    content: content || '',
    showCancel: false,
  });
}
